import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, Search, Save } from "lucide-react";

import { DataTable } from "@/components/ui/table";
import TableText from "@/components/common/TableText";
import TablePagination from "@/components/common/TablePagination";
import ColumnVisibilityDropdown from "@/components/common/ColumnVisibilityDropdown";

// CONSTANTS
const initialPermissions = [
  {
    id: 1,
    module: "User Management",
    function: "Users",
    create: true,
    modify: true,
    delete: true,
    view: true,
    email: false,
  },
  {
    id: 2,
    module: "User Management",
    function: "Roles",
    create: true,
    modify: true,
    delete: false,
    view: true,
    email: false,
  },
  {
    id: 3,
    module: "Shipment",
    function: "Bookings",
    create: true,
    modify: true,
    delete: false,
    view: true,
    email: true,
  },
  {
    id: 4,
    module: "Shipment",
    function: "Container Tracking",
    create: false,
    modify: false,
    delete: false,
    view: true,
    email: true,
  },
];

const PERMISSIONS = ["create", "modify", "delete", "view", "email", "print"];

const permissionFields = ["create", "modify", "delete", "view", "email"];

const RolePermissionsPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const role = state?.role;

  const [searchTerm, setSearchTerm] = useState("");
  const [permissions, setPermissions] = useState(initialPermissions);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // ---------------- FILTER ----------------
  const filteredPermissions = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    if (!search) return permissions;

    return permissions.filter(
      (p) =>
        p.module.toLowerCase().includes(search) ||
        p.function.toLowerCase().includes(search),
    );
  }, [permissions, searchTerm]);

  // ---------------- PAGINATION ----------------
  const paginatedPermissions = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredPermissions.slice(start, start + pageSize);
  }, [filteredPermissions, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredPermissions.length / pageSize);

  // ---------------- HELPERS ----------------
  const updatePermissions = useCallback((updater) => {
    setPermissions((prev) => prev.map(updater));
  }, []);

  // ---------------- SINGLE TOGGLE ----------------
  const handlePermissionChange = useCallback(
    (id, field) => {
      updatePermissions((item) =>
        item.id === id ? { ...item, [field]: !item[field] } : item,
      );
    },
    [updatePermissions],
  );

  // ---------------- BULK: SELECT ALL ----------------
  const handleSelectAll = useCallback((checked) => {
    setPermissions((prev) =>
      prev.map((item) => ({
        ...item,
        create: checked,
        modify: checked,
        delete: checked,
        view: checked,
        email: checked,
      })),
    );
  }, []);

  // ---------------- BULK: COLUMN ----------------
  const handleColumnToggle = useCallback(
    (field, checked) => {
      updatePermissions((item) => ({
        ...item,
        [field]: checked,
      }));
    },
    [updatePermissions],
  );

  // ---------------- MODULE LEVEL ----------------
  const handleModuleToggle = useCallback((moduleName, checked) => {
    setPermissions((prev) =>
      prev.map((item) =>
        item.module === moduleName
          ? {
              ...item,
              create: checked,
              modify: checked,
              delete: checked,
              view: checked,
              email: checked,
            }
          : item,
      ),
    );
  }, []);

  const isModuleSelected = useCallback(
    (moduleName) =>
      permissions
        .filter((r) => r.module === moduleName)
        .every((r) => permissionFields.every((f) => r[f])),
    [permissions],
  );

  const isAllSelected = useMemo(
    () =>
      permissions.length > 0 &&
      permissions.every((row) => permissionFields.every((f) => row[f])),
    [permissions],
  );

  const isColumnSelected = useCallback(
    (field) => permissions.length > 0 && permissions.every((row) => row[field]),
    [permissions],
  );

  // ---------------- RESET PAGE ----------------
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // ---------------- GROUP ----------------
  const groupedPermissions = useMemo(() => {
    return filteredPermissions.reduce((acc, item) => {
      if (!acc[item.module]) acc[item.module] = [];
      acc[item.module].push(item);
      return acc;
    }, {});
  }, [filteredPermissions]);

  // ---------------- COLUMNS ----------------
  const columns = useMemo(
    () => [
      {
        key: "module",
        label: "Module",
        width: 280,
        render: (row) => (
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-violet-500/10 flex items-center justify-center">
              <Shield className="w-5 h-5 text-violet-500" />
            </div>
            <TableText title={row.module} subtitle={row.function} />
          </div>
        ),
      },
      {
        key: "function",
        label: "Function",
        render: (row) => (
          <span className="text-slate-700 dark:text-slate-300">
            {row.function}
          </span>
        ),
      },
      ...permissionFields.map((field) => ({
        key: field,
        label: field,
        width: 100,
        render: (row) => (
          <input
            type="checkbox"
            checked={row[field]}
            onChange={() => handlePermissionChange(row.id, field)}
            className="h-4 w-4 accent-teal-500 cursor-pointer"
          />
        ),
      })),
    ],
    [handlePermissionChange],
  );

  const [visibleColumns, setVisibleColumns] = useState(
    columns.map((c) => c.key),
  );

  const filteredColumns = useMemo(
    () =>
      columns.filter(
        (c) => visibleColumns.length === 0 || visibleColumns.includes(c.key),
      ),
    [columns, visibleColumns],
  );

  // ---------------- SAVE ----------------
  const handleSave = useCallback(() => {
    const payload = {
      roleId: role?.id,
      permissions,
    };

    console.log(payload);
    alert("Permissions Saved Successfully");
  }, [permissions, role]);

  // ---------------- UI ----------------
  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-900 p-6">
      <div className="space-y-5">
        {/* HEADER */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          {/* LEFT SIDE */}
          <div className="flex items-start gap-3">
            <button
              onClick={() => navigate("/admin/roles")}
              className="p-2 border rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Shield className="w-6 h-6 text-violet-500" />
                {role?.role_name}
              </h1>

              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Manage sub modules, pricing, and configurations
              </p>
            </div>
          </div>

          {/* RIGHT ACTION */}
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-teal-500 text-white rounded-xl flex items-center gap-2 hover:bg-teal-600 transition"
          >
            <Save className="w-4 h-4" />
            Save
          </button>
        </div>

        {/* BULK ACTIONS */}
        <div className="flex flex-wrap items-center gap-4 p-3 bg-white dark:bg-slate-800 rounded-xl border">
          {/* SELECT ALL */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isAllSelected}
              onChange={(e) => handleSelectAll(e.target.checked)}
              className="accent-teal-500"
            />
            <span className="text-sm font-medium">Select All</span>
          </label>

          {/* PERMISSION FIELDS */}
          {permissionFields.map((field) => (
            <label
              key={field}
              className="flex items-center gap-2 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={isColumnSelected(field)}
                onChange={(e) => handleColumnToggle(field, e.target.checked)}
                className="accent-teal-500"
              />
              <span className="text-sm capitalize">{field}</span>
            </label>
          ))}
        </div>

        {/* MODULE SELECT (NEW FEATURE) */}
        <div className="space-y-2">
          {Object.entries(groupedPermissions).map(([moduleName]) => (
            <div
              key={moduleName}
              className="flex justify-between items-center px-4 py-2 border rounded-lg bg-white dark:bg-slate-800"
            >
              <span className="font-semibold">{moduleName}</span>

              <label className="flex gap-2 items-center text-sm">
                <input
                  type="checkbox"
                  checked={isModuleSelected(moduleName)}
                  onChange={(e) =>
                    handleModuleToggle(moduleName, e.target.checked)
                  }
                />
                Select Module
              </label>
            </div>
          ))}
        </div>

        {/* SEARCH */}
        <div className="relative w-full md:w-96">
          <Search
            className="
                      absolute left-3 top-1/2
                      h-4 w-4 -translate-y-1/2
                      text-slate-400
                    "
          />

          <input
            type="text"
            placeholder="Search roles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>

        {/* TABLE */}
        <DataTable
          data={paginatedPermissions}
          columns={filteredColumns}
          loading={false}
          emptyText="No permissions found"
          Icon={Shield}
        />

        {/* PAGINATION */}
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalRecords={filteredPermissions.length}
          currentRecord={paginatedPermissions.length}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
        />
      </div>
    </div>
  );
};

export default RolePermissionsPage;
